import React, { useEffect, useState } from "react";
import HeartLoader from "@/components/HeartLoader";
import { useParams, Link } from "react-router-dom";
import {
  Store, BadgeCheck, Users, Loader2, MapPin, Globe, ArrowLeft, Building2, DollarSign,
  TrendingUp, BarChart3, Phone, Mail, ShieldAlert, ExternalLink, Sparkles, LineChart as LineChartIcon,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import CompanyLogo from "@/components/CompanyLogo";

const scoreColor = (s) => (s == null ? "text-sec" : s >= 66 ? "text-emerald-400" : s >= 40 ? "text-amber-400" : "text-red-400");

export default function BusinessProfile() {
  const { id } = useParams();
  const { user } = useApp();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sent, setSent] = useState(null);
  const [sentBusy, setSentBusy] = useState(false);

  useEffect(() => {
    api.get(`/businesses/${id}`).then(({ data }) => {
      setB(data.business);
      if (data.business?.latest_sentiment) setSent({ from_history: true, ...data.business.latest_sentiment });
    }).catch(() => setB(null)).finally(() => setLoading(false));
  }, [id]);

  const follow = async () => {
    if (!user) { toast.error("Sign in to follow."); return; }
    try { const { data } = await api.post(`/businesses/${id}/follow`); setB((x) => ({ ...x, is_following: data.following, follower_count: data.follower_count })); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const runSentiment = async () => {
    if (!b) return;
    setSentBusy(true);
    try {
      const { data } = await api.post("/insights/business", { subject: b.name, region: "Global" });
      setSent({ title: data.title, sources: data.sources || [], ...data.result });
      toast.success("Live sentiment updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSentBusy(false); }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-5 pt-28"><HeartLoader /></div>;
  if (!b) return <div className="max-w-4xl mx-auto px-5 pt-28 text-sec">Business not found. <Link to="/businesses" className="text-brand">Back to directory</Link></div>;

  const isGlobal = b.source === "global";
  const facts = [["Industry", b.industry], ["HQ", b.hq], ["Founded", b.founded], ["Size", b.size], ["Revenue", isGlobal ? null : b.revenue], ["Type", b.org_type]].filter(([, v]) => v);
  const score = sent?.sentiment?.score ?? sent?.score ?? null;
  const label = sent?.sentiment?.label ?? sent?.label ?? "";
  const gist = sent?.headline || sent?.sentiment?.summary || "";

  const Row = ({ icon: I, label: l, value, href }) => (value ? (
    <div className="flex items-start gap-2.5 text-sm">
      <I size={15} className="text-brand mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-widest text-sec">{l}</div>
        {href ? <a href={href} target="_blank" rel="noreferrer" className="text-[var(--rud-text)] hover:text-brand break-words inline-flex items-center gap-1">{value} <ExternalLink size={11} /></a>
          : <div className="text-[var(--rud-text)] break-words">{value}</div>}
      </div>
    </div>
  ) : null);

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-24 pb-24" data-testid="business-profile">
      <Link to="/businesses" className="text-sec hover:text-brand text-sm flex items-center gap-1.5 mb-4"><ArrowLeft size={14} /> Directory</Link>
      <div className="rud-card rounded-2xl overflow-hidden">
        <div className={`h-36 relative overflow-hidden ${isGlobal ? "bg-gradient-to-br from-[var(--rud-brand)]/20 via-elevated to-elevated" : "bg-elevated"}`}>
          {b.banner && <img src={b.banner} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
          {isGlobal && !b.banner && <span className="absolute right-6 -bottom-2 font-serif text-8xl text-[var(--rud-brand)]/20 select-none">{(b.name || "?").charAt(0)}</span>}
          {isGlobal && b.rank != null && <span className="absolute top-3 left-3 text-xs font-mono text-brand bg-black/40 rounded-full px-3 py-1">#{b.rank} by global revenue</span>}
        </div>
        <div className="p-6 -mt-12">
          <div className="w-20 h-20 rounded-2xl bg-elevated border border-rud grid place-items-center overflow-hidden">
            <CompanyLogo logo={b.logo} website={b.website} name={b.name} imgClassName="w-full h-full object-contain p-2 bg-white"
              fallback={isGlobal ? <Building2 size={28} className="text-sec" /> : <Store size={28} className="text-sec" />} />
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3 mt-3">
            <div>
              <h1 className="font-serif text-3xl tracking-tight flex items-center gap-2" data-testid="bp-name">{b.name} {b.verified && <BadgeCheck size={20} className="text-brand" data-testid="bp-verified" />}</h1>
              {b.tagline && <p className="text-sec text-sm mt-1">{b.tagline}</p>}
              <div className="text-xs text-sec flex items-center gap-3 mt-1">
                <span className="flex items-center gap-1"><Users size={12} /> {b.follower_count} followers</span>
                {b.hq && <span className="flex items-center gap-1"><MapPin size={12} /> {b.hq}</span>}
                {b.website && <a href={b.website.startsWith("http") ? b.website : `https://${b.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-brand"><Globe size={12} /> Website</a>}
              </div>
            </div>
            <button data-testid="bp-follow" onClick={follow} className={`rounded-full h-9 px-5 text-sm ${b.is_following ? "btn-ghost" : "btn-primary"}`}>{b.is_following ? "Following" : "Follow"}</button>
          </div>
        </div>
      </div>

      {b.description && <div className="rud-card rounded-2xl p-6 mt-4"><div className="text-xs uppercase tracking-widest text-brand mb-2">About</div><p className="text-sm leading-relaxed whitespace-pre-wrap">{b.description}</p></div>}

      {isGlobal ? (
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div className="rud-card rounded-2xl p-6" data-testid="bp-financials">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-4"><DollarSign size={14} /> Financials</div>
            <div className="space-y-3">
              <Row icon={TrendingUp} label="Annual revenue" value={b.revenue} />
              <Row icon={BarChart3} label="Profit (net income)" value={b.profit} />
              <Row icon={DollarSign} label="Market cap" value={b.market_cap} />
              <Row icon={LineChartIcon} label={`Share price${b.ticker ? ` · ${b.ticker}${b.exchange ? " · " + b.exchange : ""}` : ""}`} value={b.share_price} />
              <Row icon={Users} label="Employees" value={b.employees} />
            </div>
            <p className="text-[10px] text-sec mt-4 flex items-center gap-1"><ShieldAlert size={11} /> Indicative figures — not real-time. Verify with official filings.</p>
          </div>
          <div className="rud-card rounded-2xl p-6" data-testid="bp-contact">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-4"><Phone size={14} /> Contact & HQ</div>
            <div className="space-y-3">
              <Row icon={Globe} label="Website" value={b.website} href={b.website ? (b.website.startsWith("http") ? b.website : `https://${b.website}`) : ""} />
              <Row icon={Phone} label="Phone" value={b.phone} />
              <Row icon={Mail} label="Email" value={b.email} href={b.email ? `mailto:${b.email}` : ""} />
              <Row icon={MapPin} label="Headquarters" value={b.hq} />
            </div>
            <p className="text-[10px] text-sec mt-4 flex items-center gap-1"><ShieldAlert size={11} /> Contact details are AI-compiled from public info and unverified — confirm before contacting.</p>
          </div>
        </div>
      ) : (
        <>
          {facts.length > 0 && (
            <div className="rud-card rounded-2xl p-6 mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
              {facts.map(([k, v]) => <div key={k}><div className="text-[11px] uppercase tracking-widest text-sec">{k}</div><div className="text-sm mt-0.5">{v}</div></div>)}
            </div>
          )}
          {b.key_people && <div className="rud-card rounded-2xl p-6 mt-4"><div className="text-xs uppercase tracking-widest text-brand mb-2">Leadership</div><p className="text-sm text-sec">{b.key_people}</p></div>}
        </>
      )}

      {/* Sentiment & Signals */}
      <div className="rud-card rounded-2xl p-6 mt-4" data-testid="bp-sentiment">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Sparkles size={14} /> Global Sentiment of {b.name}</div>
          <button data-testid="bp-run-sentiment" onClick={runSentiment} disabled={sentBusy} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-2 disabled:opacity-60">
            {sentBusy ? <Loader2 size={15} className="animate-spin" /> : <TrendingUp size={15} />} {sent ? "Refresh" : "Get live sentiment"}
          </button>
        </div>
        {sent ? (
          <div className="mt-4 space-y-4">
            <div className="flex items-center gap-3">
              <span className={`text-2xl font-semibold tabular-nums ${scoreColor(score)}`} data-testid="bp-sentiment-score">{score ?? "—"}<span className="text-sm text-sec">/100</span></span>
              {label && <span className="text-sm text-sec uppercase tracking-wide">{label}</span>}
            </div>
            {gist && <p className="text-[var(--rud-text)]" data-testid="bp-sentiment-gist">{gist}</p>}
            {sent.sentiment?.summary && sent.sentiment.summary !== gist && <p className="text-sm text-sec">{sent.sentiment.summary}</p>}
            {sent.sources?.length > 0 && (
              <div data-testid="bp-sentiment-sources">
                <div className="text-[11px] uppercase tracking-widest text-sec mb-2">Sources</div>
                <div className="space-y-2">
                  {sent.sources.map((s, i) => (
                    <a key={i} href={s.url} target="_blank" rel="noreferrer" className="block rounded-lg border border-rud bg-elevated p-3 hover:border-brand/50 transition-colors">
                      <div className="text-sm text-[var(--rud-text)] flex items-center gap-1.5"><ExternalLink size={12} className="text-brand shrink-0" /> {s.title}</div>
                      {s.snippet && <p className="text-xs text-sec mt-1 line-clamp-2">{s.snippet}</p>}
                      <div className="text-[10px] text-sec mt-1 truncate">{s.url}{s.published ? ` · ${s.published}` : ""}</div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-sec mt-3">Run a live, web-grounded read on {b.name}'s current public sentiment — with the sources behind it.</p>
        )}
      </div>
    </div>
  );
}
